import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CoreModule } from './core/core.module';
import { HomeModule } from './home/home.module';
import { LoginModule } from './login/login.module';
import { OrderModule } from './order/order.module';
import { PaymentResultModule } from './payment-result/payment-result.module';
import { SharedModule } from './shared/shared.module';
import { HTTP_INTERCEPTORS, HttpBackend } from '@angular/common/http';
import { AppConfigService } from '@core/services/app-config.service';
import { AppLocaleService } from '@core/services/app-locale.service';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';

/* Locale data */
import { registerLocaleData } from '@angular/common';
import localeEnAu from '@angular/common/locales/en-AU';
import localeFi from '@angular/common/locales/fi';
import localeSv from '@angular/common/locales/sv';
/* End Locale data */

import { CassHttpInterceptorService } from '@core/services/cass-http-interceptor.service';
import { CassMissingTranslationHandlerService } from '@core/services/cass-missing-translation-handler.service';
import { UsersModule } from '@users/users.module';
import { MultiTranslateHttpLoader } from "ngx-translate-multi-http-loader";
import { UserProfilesService } from './core/services/user-profiles.service';
import { ChooseAdvertiserComponent } from './customers/choose-advertiser/choose-advertiser.component';
import { CustomerAddressPageComponent } from './customers/customer-address/customer-address.component';
import { CustomerContactPageComponent } from './customers/customer-contact-page/customer-contact-page.component';
import { CustomerEmailPageComponent } from './customers/customer-email/customer-email.component';
import { CustomerListFilterComponent } from './customers/customer-list-filter/customer-list-filter.component';
import { CustomerPageComponent } from './customers/customer-page/customer-page.component';
import { CustomerWebsitePageComponent } from './customers/customer-website/customer-website.component';
import { CustomersListComponent } from './customers/customers-list/customers-list.component';
import { CustomersPageComponent } from './customers/customers-page/customers-page.component';
import { DashboardComponent } from './dashboard/dashboard.component';
import { HelpPageComponent } from './help-page/help-page.component';
import { NotificationsListComponent } from './messages/notifications-list/notifications-list.component';
import { NotificationsPageComponent } from './messages/notifications-page/notifications-page.component';
import { MyAdsListComponent } from './my-ads/my-ads-list/my-ads-list.component';
import { MyAdsPageComponent } from './my-ads/my-ads-page/my-ads-page.component';
import { ProfileModule } from './profile/profile.module';
import { SelfTestComponent } from './self-test/self-test.component';
import { AngularMaterialModule } from './shared/angular-material.module';
import { AdvertiserIdChipComponent } from './shared/components/advertiser-id-chip/advertiser-id-chip.component';
import { MessagesModule } from './messages/messages.module';
import { ExternalPlannerLoginComponent } from '@app/external-planner-login/external-planner-login.component';


export function HttpLoaderFactory(httpBackend: HttpBackend) {
	return new MultiTranslateHttpLoader(httpBackend, [
		{ prefix: "./assets/i18n/", suffix: ".json" },
		{ prefix: "./branding/i18n/", suffix: ".json" },
	]);
}

/* Locales that are to be supported should be imported above and registered here */
registerLocaleData(localeEnAu);
registerLocaleData(localeFi);
registerLocaleData(localeSv);

@NgModule({
	declarations: [
	    AdvertiserIdChipComponent,
		AppComponent,
  		ChooseAdvertiserComponent,
  		CustomerAddressPageComponent,
  		CustomerContactPageComponent,
  		CustomerEmailPageComponent,
		CustomerListFilterComponent,
		CustomerPageComponent,
  		CustomersListComponent,
  		CustomersPageComponent,
  		CustomerWebsitePageComponent,
		DashboardComponent,
		HelpPageComponent,
		MyAdsListComponent,
		MyAdsPageComponent,
		NotificationsListComponent,
		NotificationsPageComponent,
		SelfTestComponent,
  		ExternalPlannerLoginComponent,

	],
	imports: [
		AngularMaterialModule,
		AppRoutingModule,
		BrowserAnimationsModule,
		BrowserModule,
		CoreModule,
		HomeModule,
		LoginModule,
		MessagesModule,
		OrderModule,
		PaymentResultModule,
		ProfileModule,
		ReactiveFormsModule,
		SharedModule,
		TranslateModule.forRoot({
			defaultLanguage: 'en',
			useDefaultLang: false, /* use the MissingTranslationHandler when a string is missing so we can intercept */
			missingTranslationHandler: {
				provide: MissingTranslationHandler,
				useClass: CassMissingTranslationHandlerService
			},
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpBackend]
			},
			isolate: false
		}),
		UsersModule
	],
	providers: [
		{
			provide: APP_INITIALIZER,
			multi: true,
			deps: [AppConfigService, AppLocaleService, UserProfilesService],
			useFactory: (appConfigService: AppConfigService, appLocaleService: AppLocaleService, userProfilesService: UserProfilesService) => {
				return () => {
					userProfilesService.init();
					appLocaleService.init().subscribe();
					return appConfigService.init();
				};
			}
		},
		Location,
		{
			provide: LocationStrategy,
			useClass: PathLocationStrategy
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: CassHttpInterceptorService,
			multi: true
		}
	],
	bootstrap: [AppComponent]
})
export class AppModule { }
